import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import PasswordField from './PasswordField';
import './Login.css';
import { formatPhoneInput } from '../utils/phone';

function RequestAccess() {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [tenantCode, setTenantCode] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationUrl, setVerificationUrl] = useState('');

  const isLocked = isSubmitting || requestSubmitted;
  const needsBusinessProfile = !String(tenantCode || '').trim();

  async function handleSubmit(e) {
    e.preventDefault();
    if (isLocked) return;

    setError('');
    setSuccess('');
    setVerificationUrl('');
    setIsSubmitting(true);

    try {
      const res = await axios.post('/api/auth/request-access', {
        email: String(email || '').trim(),
        fullName: String(fullName || '').trim(),
        password,
        phone: String(phone || '').trim(),
        tenantCode: String(tenantCode || '').trim(),
        businessName: String(businessName || '').trim(),
        businessEmail: String(businessEmail || '').trim(),
        businessPhone: String(businessPhone || '').trim(),
        billingAddress: String(billingAddress || '').trim(),
        shippingAddress: String(shippingAddress || '').trim()
      });

      setRequestSubmitted(true);
      setSuccess('Request submitted. Your verification email was sent. Once you verify your email, your request will be reviewed by admin. You will receive an email when your request is approved or denied.');
      setVerificationUrl(String(res?.data?.verificationUrl || '').trim());
      setPassword('');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to submit access request';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="login-container request-access-container">
      <h2>Request Access</h2>
      {requestSubmitted ? (
        <div
          style={{
            marginBottom: '1rem',
            fontSize: '0.98rem',
            fontWeight: 600,
            color: '#0b4f8a',
            backgroundColor: '#eef6ff',
            border: '1px solid #9dc9ff',
            borderRadius: 6,
            padding: '0.75rem 0.85rem',
            lineHeight: 1.45
          }}
        >
          Your request is being processed. Please check your email and click the verification link. This page is now locked to prevent duplicate requests.
          {verificationUrl ? (
            <div style={{ marginTop: '0.6rem' }}>
              <a href={verificationUrl} target="_blank" rel="noreferrer">
                Verify now (dev/test fallback)
              </a>
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        style={{
          marginBottom: '1rem',
          fontSize: '0.92rem',
          color: '#0b4f8a',
          backgroundColor: '#eef6ff',
          border: '1px solid #cfe7ff',
          borderRadius: 6,
          padding: '0.6rem 0.75rem'
        }}
      >
        If you already have a tenant code, enter it for faster review. If not, provide business profile details so admin can match or create your tenant.
      </div>
      <form onSubmit={handleSubmit} className="login-form request-access-form">
        <div className="request-access-row">
          <div className="form-group request-access-col">
            <label>Email</label>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLocked}
              required
            />
          </div>

          <div className="form-group request-access-col">
            <label>Full Name (optional)</label>
            <input
              type="text"
              placeholder="Enter your full name"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              disabled={isLocked}
            />
          </div>
        </div>

        <div className="request-access-row">
          <div className="form-group request-access-col">
            <label>Phone (optional)</label>
            <input
              type="tel"
              placeholder="Text-capable phone number"
              value={phone}
              onChange={e => setPhone(formatPhoneInput(e.target.value))}
              maxLength={14}
              disabled={isLocked}
            />
          </div>

          <div className="form-group request-access-col">
            <label>Tenant Code (optional)</label>
            <input
              type="text"
              placeholder="Enter tenant code if provided"
              value={tenantCode}
              onChange={e => setTenantCode(String(e.target.value || '').toUpperCase())}
              disabled={isLocked}
              maxLength={24}
            />
          </div>
        </div>

        <div
          style={{
            marginTop: '0.15rem',
            marginBottom: '0.15rem',
            fontSize: '0.9rem',
            color: '#475569'
          }}
        >
          {needsBusinessProfile
            ? 'Tenant code not provided. Business profile fields below are required.'
            : 'Tenant code provided. Business profile fields below are optional but helpful.'}
        </div>

        <div className="request-access-row">
          <div className="form-group request-access-col">
            <label>Business Name</label>
            <input
              type="text"
              placeholder="Business legal name"
              value={businessName}
              onChange={e => setBusinessName(e.target.value)}
              disabled={isLocked}
              required={needsBusinessProfile}
            />
          </div>

          <div className="form-group request-access-col">
            <label>Business Email</label>
            <input
              type="email"
              placeholder="billing@company.com"
              value={businessEmail}
              onChange={e => setBusinessEmail(e.target.value)}
              disabled={isLocked}
              required={needsBusinessProfile}
            />
          </div>
        </div>

        <div className="request-access-row">
          <div className="form-group request-access-col">
            <label>Business Phone</label>
            <input
              type="tel"
              placeholder="(555) 123-4567"
              value={businessPhone}
              onChange={e => setBusinessPhone(formatPhoneInput(e.target.value))}
              maxLength={14}
              disabled={isLocked}
              required={needsBusinessProfile}
            />
          </div>

          <div className="form-group request-access-col">
            <label>Password</label>
            <PasswordField
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Create a password"
              name="requestAccessPassword"
              autoComplete="new-password"
              disabled={isLocked}
              required
              ariaLabel="request access password"
            />
          </div>
        </div>

        <div className="form-group request-access-col-full">
          <label>Billing Address</label>
          <textarea
            placeholder="Street, city, state, zip"
            value={billingAddress}
            onChange={e => setBillingAddress(e.target.value)}
            disabled={isLocked}
            required={needsBusinessProfile}
            rows={2}
            style={{ resize: 'vertical' }}
          />
        </div>

        <div className="form-group request-access-col-full">
          <label>Shipping Address (optional)</label>
          <textarea
            placeholder="If different from billing"
            value={shippingAddress}
            onChange={e => setShippingAddress(e.target.value)}
            disabled={isLocked}
            rows={2}
            style={{ resize: 'vertical' }}
          />
        </div>

        <button type="submit" className="login-btn" disabled={isLocked}>
          {isSubmitting ? 'Sending verification…' : requestSubmitted ? 'Request Submitted' : 'Send Verification Email'}
        </button>

        {error ? <div className="form-error" role="alert">{error}</div> : null}
        {success ? <div style={{ marginTop: '0.5rem', color: '#0a7f37', fontSize: '0.9rem' }}>{success}</div> : null}
      </form>

      {!requestSubmitted ? (
        <div className="login-links" role="group" aria-label="Access request links">
          <Link to="/login" className="link-btn">Back to Sign In</Link>
        </div>
      ) : null}
    </div>
  );
}

export default RequestAccess;
